import {
  Button,
  CrudMode,
  UpdateIconButton,
  formatPhoneNumber,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  pn,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  Permission,
  PermissionAction,
  SubPartnerListDto,
  SubPartnerListFilter,
  SubPartnerListResource,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import "moment-timezone";
import { subPartnerPageRoute } from "pages/match-utility/sub-partners/SubPartnerPage/SubPartnerPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { formatAddress, isAuthorized } from "services";
import ApiService from "services/apiService";

export const SubPartners = (props: { initialLoad: boolean; partnerId: number; onGetList: () => void }) => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<SubPartnerListFilter>({
    subPartnerFilter: { partnerId: props.partnerId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<SubPartnerListDto>("name"), isDesc: false }],
  });
  const [listItems, setListItems] = useState<SubPartnerListDto[]>([]);
  const [resource, setResource] = useState<SubPartnerListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.subPartners.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.subPartners.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  const goToSubPartnerPage = (id: number, crudMode: CrudMode) => {
    navigate({
      pathname: subPartnerPageRoute.getPath({ id: id, crudMode: crudMode, screenType: "dialog" }),
      search: subPartnerPageRoute.getOptionalParams?.({
        partnerId: props.partnerId,
      }),
    });
  };

  return (
    <>
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent="flex-end">
            {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
              <Button onClick={() => goToSubPartnerPage(0, "edit")} variant="outlined">
                + New
              </Button>
            )}
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<SubPartnerListDto>("name"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<SubPartnerListDto>("contact"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<SubPartnerListDto>("phone"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<SubPartnerListDto>("fax"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<SubPartnerListDto>("address"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems.map((listItem) => (
                  <TableRow key={listItem.id} onClick={() => goToSubPartnerPage(listItem.id, "view")}>
                    <TableCell>{listItem?.name}</TableCell>
                    <TableCell>{listItem.contact}</TableCell>
                    <TableCell>{formatPhoneNumber(listItem.phone)}</TableCell>
                    <TableCell>{formatPhoneNumber(listItem.fax)}</TableCell>
                    <TableCell>{formatAddress(listItem.address)}</TableCell>

                    <TableCell type="action">
                      {isAuthorized(ROOT_PERMISSION, PermissionAction.Update) && (
                        <UpdateIconButton onClick={() => goToSubPartnerPage(listItem.id, "edit")} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider
        value={{
          loadList: (options) => {
            loadList(listFilter, { resetPage: options?.resetPage });
            props.onGetList();
          },
        }}
      >
        {/* Outlet will only render for the designated route to prevent conflicts with others on parent Page */}
        {matchTemplatePath(subPartnerPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
